// The six kinds of condition the unified filter tree can hold. All six nest
// at any depth (the backend evaluates any lane inside an OR group), so the
// lane is signalled by a coloured glyph square on the row (LaneMarkerAtom)
// rather than by section placement — see specs/unified-filters-v2.md
// "B4 · Lane markers and the joiner gutter" and ctx-atoms.jsx `FILTER_LANES`.
//
// `measure` has no `kind` discriminant of its own in `FilterGroupLeaf`
// (types/filters.d.ts) — it is a `relationship` leaf on the `measures_into`
// edge, presented under its own lane per the spec's "measure lane is a
// presentation rule, not a type" decision. This type is UI-presentation
// only; it never substitutes for `FilterGroupLeaf['kind']`.
export type FilterLaneKind =
  | 'aspect'
  | 'identity'
  | 'perspective'
  | 'relationship'
  | 'arc'
  | 'measure';

export interface FilterLaneMeta {
  /** Iconify name (lucide collection, offline-bundled — see GlyphAtom) */
  icon: string;
  /** i18n key for the lane's short label, used as tooltip/aria-label */
  labelKey: string;
  /** CSS custom-property name suffix — resolves `--lane-<token>-fg`/`-bg` */
  token: string;
}

export const FILTER_LANE_META: Record<FilterLaneKind, FilterLaneMeta> = {
  aspect: { icon: 'lucide:tag', labelKey: 'filters.lane.aspect.badge', token: 'aspect' },
  identity: { icon: 'lucide:shapes', labelKey: 'filters.lane.identity.badge', token: 'identity' },
  perspective: { icon: 'lucide:eye', labelKey: 'filters.lane.perspective.badge', token: 'perspective' },
  relationship: { icon: 'lucide:waypoints', labelKey: 'filters.lane.relationship.badge', token: 'relationship' },
  arc: { icon: 'lucide:git-branch', labelKey: 'filters.lane.arc.badge', token: 'arc' },
  measure: { icon: 'lucide:target', labelKey: 'filters.lane.measure.badge', token: 'measure' },
};

export const DEFAULT_FILTER_LANE: FilterLaneKind = 'aspect';

/**
 * How many lanes there are, derived from the map rather than written down.
 * Copy that states the count reads it from here — the hint under the advanced
 * tree said "all five lanes" for as long as it took `identity` and `measure` to
 * be added, and nothing could have noticed.
 */
export const FILTER_LANE_COUNT = Object.keys(FILTER_LANE_META).length;
